import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack
} from '@mui/material';

import GhostButton from '../../shared/button/GhostButton';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { BodyText, CaptionText } from '../../shared/typography/PrimaryTypography';
import {
  fetchMetricAliases,
  resolveMetricAlias,
  toProblemDetail
} from '../../../services/syntiro_honey/data_ingestion.service';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { MetricAliasItem, ProblemDetail } from '../../../types/syntiro_honey';
import MetricAutocomplete from '../review/MetricAutocomplete';

interface ResolveRuleDialogProps {
  /** The (usually contested) rule whose label is being resolved. */
  rule: MetricAliasItem;
  /** Close without resolving. */
  onClose: () => void;
  /** Called after a successful resolution so the parent refreshes its list. */
  onResolved: () => void;
}

/** One competing metric in the label's confirmation history. */
interface CompetingMetric {
  /** The candidate metric id. */
  metricId: string;
  /** The candidate metric's catalogue name. */
  metricName: string;
  /** How many confirmations backed this metric. */
  confirmations: number;
  /** Who confirmed it last. */
  lastBy: string;
  /** When it was last confirmed (ISO 8601). */
  lastAt: string;
}

/** Rolls the label's full history up into one row per competing metric, newest first. */
function toCompetingMetrics(history: Array<MetricAliasItem>): Array<CompetingMetric> {
  const byMetric = new Map<string, CompetingMetric>();
  history.forEach((entry) => {
    const existing = byMetric.get(entry.metricId);
    if (existing === undefined) {
      byMetric.set(entry.metricId, {
        metricId: entry.metricId,
        metricName: entry.metricName,
        confirmations: 1,
        lastBy: entry.createdByName ?? entry.createdBy,
        lastAt: entry.createdAt
      });
      return;
    }
    existing.confirmations += 1;
    if (entry.createdAt > existing.lastAt) {
      existing.lastAt = entry.createdAt;
      existing.lastBy = entry.createdByName ?? entry.createdBy;
    }
  });
  return [...byMetric.values()].sort((a, b) => b.lastAt.localeCompare(a.lastAt));
}

/**
 * The EXPLICIT HUMAN RESOLUTION of a label (#43 / ADR-0003): shows every metric
 * the label was ever confirmed to (who, when, how often), lets the reviewer pick
 * the winner — or any other catalogue metric — and re-arms automatic mapping.
 * This is the one way out of "contested": the collision rule never picks a
 * winner on its own.
 * @returns {JSX.Element}
 */
export default function ResolveRuleDialog({ rule, onClose, onResolved }: ResolveRuleDialogProps) {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [history, setHistory] = useState<Array<MetricAliasItem> | null>(null);
  const [selected, setSelected] = useState<{ id: string; name: string | null }>({
    id: rule.metricId,
    name: rule.metricName
  });
  const [busy, setBusy] = useState<boolean>(false);
  const [problem, setProblem] = useState<ProblemDetail | null>(null);

  useEffect(() => {
    let cancelled = false;
    // The label's FULL confirmation history (any status): the competing
    // confirmations are the evidence the resolution decision needs.
    fetchMetricAliases({
      companyId: rule.companyId,
      normalisedLabel: rule.normalisedLabel,
      limit: 200
    })
      .then((result) => {
        if (!cancelled) {
          setHistory(result.items);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setProblem(toProblemDetail(error));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rule.companyId, rule.normalisedLabel]);

  const competing = useMemo(() => toCompetingMetrics(history ?? []), [history]);

  const handleResolve = async (): Promise<void> => {
    setBusy(true);
    setProblem(null);
    try {
      await resolveMetricAlias(rule.companyId, rule.normalisedLabel, selected.id);
      setFeedbackData({
        state: true,
        type: 'success',
        message: `"${rule.rawLabel}" now maps to ${
          selected.name ?? 'the chosen metric'
        } for this client — automatic mapping is back on.`
      });
      onResolved();
    } catch (error: unknown) {
      setProblem(toProblemDetail(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Resolve &ldquo;{rule.rawLabel}&rdquo;</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <BodyText>
          Pick the metric this label means for this client. Every earlier confirmation is retired
          and the label maps automatically again from the next upload.
        </BodyText>
        {problem !== null && (
          <Alert severity="error">{problem.detail ?? problem.title ?? 'Resolving failed.'}</Alert>
        )}
        {history === null ? (
          <Stack alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <Stack spacing={0.5}>
            <CaptionText weight="Medium">Confirmations so far</CaptionText>
            <List dense disablePadding>
              {competing.map((candidate) => (
                <ListItemButton
                  key={candidate.metricId}
                  selected={candidate.metricId === selected.id}
                  onClick={() =>
                    setSelected({ id: candidate.metricId, name: candidate.metricName })
                  }
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemText
                    disableTypography
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CaptionText weight="Medium" sx={{ flex: 1 }}>
                          {candidate.metricName}
                        </CaptionText>
                        <Chip
                          label={`${candidate.confirmations}×`}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    }
                    secondary={
                      <CaptionText>
                        last confirmed by {candidate.lastBy} on{' '}
                        {new Date(candidate.lastAt).toLocaleString()}
                      </CaptionText>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Stack>
        )}
        <MetricAutocomplete
          label="Or pick another metric"
          selectedId={selected.id}
          selectedLabel={selected.name}
          onSelect={(metric) => {
            if (metric !== null) {
              setSelected({ id: metric.id, name: metric.name });
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <GhostButton dark onClick={onClose} disabled={busy}>
          Cancel
        </GhostButton>
        <PrimaryButton onClick={() => void handleResolve()} disabled={busy} loading={busy}>
          Use this metric from now on
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
}
